import PropTypes from "prop-types";
import React from "react";
import { Tabs } from "antd";

import {
    Layout,
    Breadcrumb,
    Button,
    Avatar,
    Dropdown,
    Space,
    Typography,
    Flex,
} from "antd";
import {
    ArrowLeftOutlined,
    EditOutlined,
    CopyOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { EllipsisVertical } from "lucide-react";

export default function DetailTabs({ defaultValue, children, tabs, onDelete, onCopy, actions = [], metadata = [] }) {
    // Extract content/className from DetailTabsContent children by their value prop.
    // Children are never mounted here; we only read their props as data.
    const contentMap = {};
    const classNameMap = {};
    React.Children.forEach(children, (child) => {
        if (child?.props?.value !== undefined) {
            contentMap[child.props.value] = child.props.children;
            classNameMap[child.props.value] = child.props.className;
        }
    });

    const dropdownItems = [
        ...(onCopy
            ? [
                {
                    key: "copy",
                    icon: <CopyOutlined />,
                    label: "Копировать",
                    onClick: onCopy,
                },
            ]
            : []),
        ...actions.map((action, index) => ({
            key: `action-${index}`,
            icon: action.icon,
            label: action.label,
            onClick: () => action.onClick?.(),
        })),
        ...(onDelete
            ? [
                {
                    key: "delete",
                    icon: <DeleteOutlined />,
                    label: "Удалить",
                    danger: true,
                    onClick: onDelete,
                },
            ]
            : []),
    ];
    const items = tabs.map((tab) => ({
        key: tab.value,
        label: (
            <span className="flex items-center gap-1.5">
                {tab.icon && <span>{tab.icon}</span>}
                {tab.label}
                {tab.badge != null && (
                    <span className="ml-1 text-xs">{tab.badge}</span>
                )}
            </span>
        ),
        children: (
            <div className={classNameMap[tab.value]}>
                {contentMap[tab.value]}
            </div>
        ),
    }));

    const moreActions = dropdownItems.length > 0 ? (
        <Dropdown menu={{ items: dropdownItems }} trigger={["click"]}>
            <Button
                aria-label="Дополнительные действия"
                className="detail-hero__more-btn"
                icon={<EllipsisVertical size={16} />}
                title="Дополнительные действия"
            />
        </Dropdown>
    ) : null;

    return (
        <Tabs
            defaultActiveKey={defaultValue}
            items={items}
            type="card"
            tabBarExtraContent={moreActions ? { right: moreActions } : undefined}
        />
    );
}

DetailTabs.propTypes = {
    defaultValue: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.node,
            badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
};

// Data carrier only — rendered content is lifted into the Tabs items by DetailTabs
export const DetailTabsContent = ({ children }) => children ?? null;

DetailTabsContent.propTypes = {
    value: PropTypes.string.isRequired,
    className: PropTypes.string,
    children: PropTypes.node,
};
